"use client";

import { useEffect } from "react";

// Highlight bij hover (klikbaar signaleren) vs. actieve bewerk-outline — tekst.
const HOVER_OUTLINE = "2px dashed #d4896a";
const EDIT_OUTLINE = "2px dashed #720917";

// Afbeeldingen krijgen een eigen kleur (blauw i.p.v. het tekst-oranje/bordeaux)
// zodat direct duidelijk is dat een klik hier iets anders doet (bestand kiezen
// i.p.v. inline bewerken).
const IMAGE_HOVER_OUTLINE = "3px solid #2f6fed";
const IMAGE_BUSY_OPACITY = "0.4";

/**
 * Laag 2 van de live-bewerking (klik-en-bewerk-brug): draait binnen de
 * <iframe src="/pad?liveEdit=1"> die het admin-paneel toont. Activeert zich
 * ALLEEN als de pagina in een iframe zit óf ?liveEdit=1 in de URL staat.
 *
 * Twee onafhankelijke klik-gedragingen, op twee losse attributen:
 * - [data-t-key]: tekst. Klik → contentEditable aan, bij blur / Cmd+S een
 *   postMessage SAVE_INLINE_TEXT naar de ouder met de nieuwe tekst.
 * - [data-i-key]: afbeeldingen. Klik → GEEN contentEditable, in plaats
 *   daarvan een verborgen <input type="file"> die opent; na bestandskeuze
 *   wordt die als data-URL gelezen en als SAVE_INLINE_IMAGE naar de ouder
 *   gestuurd. De ouder doet de echte upload + opslag (via de admin-API's) en
 *   stuurt UPDATE_PREVIEW terug.
 *
 * Alleen textContent/.src — nooit innerHTML (zie beveiligingsmodel).
 * postMessage altijd met expliciete targetOrigin (nooit '*').
 */
export default function VisualEditorBridge() {
  useEffect(() => {
    // De activatie-check staat BINNEN de effect-callback (niet als vroege
    // return vóór de hook) zodat de rules-of-hooks niet geschonden worden —
    // de hook wordt hierdoor altijd aangeroepen, alleen het gedrag erbinnen
    // is conditioneel.
    // De live-editor van de Black Friday- en selectiepagina's (?bewerk=1, components/live/useBewerkBrug.ts) heeft een
    // eigen brug; deze oude zou daar de teksten van de kop en voet bewerkbaar maken zonder dat iets ze opslaat.
    if (window.location.search.includes("bewerk=1")) return;
    const inIframe = window !== window.parent;
    const hasLiveEdit = window.location.search.includes("liveEdit=1");
    if (!inIframe && !hasLiveEdit) return;

    // Bewerk-modus: de cookiebanner (vast, z-index 9999, dekt de hele pagina)
    // blokkeert anders elke klik in de preview tot er een keuze in
    // localStorage staat. Verbergen via de vaste class op de buitenste laag van
    // ConsentBanner. De banner zet bovendien body{overflow:hidden} zolang hij
    // "open" staat — zonder de tweede regel zou de preview niet scrollen.
    // Die regel reset bewust alleen de y-as: de globale body{overflow-x:hidden}
    // (globals.css) blijft zo gelden, anders krijgt de preview een horizontale
    // scrollbalk die de echte site nooit toont.
    // (Aparte regels: ondersteunt een browser :has() niet, dan valt alleen
    // de tweede weg.) Alleen hier, dus nooit voor gewone bezoekers.
    const editStyle = document.createElement("style");
    editStyle.setAttribute("data-mow-live-edit", "");
    editStyle.textContent =
      ".mow-consent-overlay{display:none!important}" +
      "body:has(.mow-consent-overlay){overflow-y:auto!important}";
    document.head.appendChild(editStyle);

    // ---- Tekst (bestaand gedrag, ongewijzigd) ----

    let activeEl: HTMLElement | null = null;
    let originalText = "";
    let originalOutline = "";

    // Overrides die de ouder ons stuurde (SYNC_TEXTS / UPDATE_PREVIEW) of die
    // hier net zijn ingetikt, key -> waarde. Nodig om elementen die pas NA de
    // eerste scan renderen (zie de MutationObserver onderaan) alsnog de juiste
    // tekst/foto te geven, zonder dat een net opgeslagen tekst heel even
    // terugspringt naar de oude waarde.
    const knownOverrides = new Map<string, string>();

    function closestEditable(target: EventTarget | null): HTMLElement | null {
      if (!(target instanceof Element)) return null;
      return target.closest("[data-t-key]") as HTMLElement | null;
    }

    function onMouseOver(e: MouseEvent) {
      const el = closestEditable(e.target);
      if (!el || el === activeEl) return;
      el.style.outline = HOVER_OUTLINE;
      el.style.cursor = "pointer";
    }

    function onMouseOut(e: MouseEvent) {
      const el = closestEditable(e.target);
      if (!el || el === activeEl) return;
      el.style.outline = "";
    }

    function onElInput(this: HTMLElement) {
      const key = this.getAttribute("data-t-key");
      if (!key) return;
      window.parent.postMessage(
        { type: "INLINE_INPUT_CHANGE", id: key, value: this.innerText },
        window.location.origin
      );
    }

    function stopEditing(el: HTMLElement) {
      el.contentEditable = "false";
      el.style.outline = originalOutline;
      el.removeEventListener("input", onElInput);
      el.removeEventListener("blur", onElBlur);
      el.removeEventListener("keydown", onElKeyDown as EventListener);
      if (activeEl === el) activeEl = null;
    }

    function onElBlur(e: FocusEvent) {
      // currentTarget = het element waar de listener aan hangt (zelfde als `this`).
      const el = e.currentTarget as HTMLElement;
      const key = el.getAttribute("data-t-key");
      const value = el.innerText.trim();
      // Alleen opslaan als er echt iets veranderd is. Zonder deze check stuurt
      // ELKE blur een save — dus ook een klik-om-te-bekijken zonder wijziging —
      // en dat overschrijft een reeds bestaande live-override stilzwijgend
      // terug naar de (in de iframe getoonde) standaardtekst.
      if (key && value !== originalText.trim()) {
        knownOverrides.set(key, value);
        window.parent.postMessage(
          { type: "SAVE_INLINE_TEXT", id: key, value },
          window.location.origin
        );
      }
      stopEditing(el);
    }

    function onElKeyDown(e: KeyboardEvent) {
      const el = e.currentTarget as HTMLElement;
      const key = el.getAttribute("data-t-key");

      // Cmd/Ctrl+S: opslaan i.p.v. de browser-save-dialoog tonen. Zelfde
      // dirty-check als onElBlur — zie de toelichting daar.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        const value = el.innerText.trim();
        if (key && value !== originalText.trim()) {
          knownOverrides.set(key, value);
          window.parent.postMessage(
            { type: "SAVE_INLINE_TEXT", id: key, value },
            window.location.origin
          );
        }
        stopEditing(el);
        return;
      }

      // Escape: bewerken annuleren, tekst terug naar de waarde van vóór het klikken.
      if (e.key === "Escape") {
        e.preventDefault();
        // De cookiebanner luistert op document naar Escape en legt dat vast als
        // "alleen noodzakelijk" (ook als hij hier verborgen is): niet laten
        // doorborrelen, dit Escape gaat alleen over het annuleren van de bewerking.
        e.stopPropagation();
        el.innerText = originalText;
        stopEditing(el);
      }
    }

    function onClick(e: MouseEvent) {
      const el = closestEditable(e.target);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();

      if (activeEl === el) return;
      if (activeEl) stopEditing(activeEl);

      activeEl = el;
      originalText = el.innerText;
      originalOutline = el.style.outline;

      el.contentEditable = "true";
      el.style.outline = EDIT_OUTLINE;
      el.focus();

      el.addEventListener("input", onElInput);
      el.addEventListener("blur", onElBlur);
      el.addEventListener("keydown", onElKeyDown as EventListener);
    }

    // ---- Afbeeldingen (nieuw) ----

    // Eén hergebruikte verborgen file-input voor alle [data-i-key]-klikken.
    let fileInput: HTMLInputElement | null = null;
    let pendingImageEl: HTMLElement | null = null;
    let pendingImageKey: string | null = null;
    // key -> lopende "bezig"-vangnet-timer, zodat een uitblijvende
    // UPDATE_PREVIEW (bv. door een mislukte upload) de opacity niet voor
    // altijd op "bezig" laat staan.
    const imageBusyTimers = new Map<string, ReturnType<typeof setTimeout>>();

    // Losse, zwevende badge ("klik om te vervangen") die bij hover over een
    // afbeeldingselement in beeld komt — een <img> heeft meestal geen
    // positioned parent om een overlay in te nestelen, dus een los
    // position:fixed-element dat we handmatig positioneren is de simpelste,
    // veiligste aanpak (geen wijziging aan de DOM-structuur van de pagina zelf).
    const badge = document.createElement("div");
    badge.textContent = "Klik om te vervangen";
    badge.style.cssText =
      "position:fixed;z-index:2147483647;pointer-events:none;display:none;" +
      "background:#2f6fed;color:#fff;font:600 12px/1.4 -apple-system,BlinkMacSystemFont,sans-serif;" +
      "padding:3px 8px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.25);";
    document.body.appendChild(badge);

    function closestImageEditable(target: EventTarget | null): HTMLElement | null {
      if (!(target instanceof Element)) return null;
      return target.closest("[data-i-key]") as HTMLElement | null;
    }

    function showBadge(el: HTMLElement) {
      const rect = el.getBoundingClientRect();
      badge.style.left = `${Math.max(4, rect.left + 6)}px`;
      badge.style.top = `${Math.max(4, rect.top + 6)}px`;
      badge.style.display = "block";
    }

    function hideBadge() {
      badge.style.display = "none";
    }

    function onImageMouseOver(e: MouseEvent) {
      const el = closestImageEditable(e.target);
      if (!el) return;
      el.style.outline = IMAGE_HOVER_OUTLINE;
      el.style.cursor = "pointer";
      showBadge(el);
    }

    function onImageMouseOut(e: MouseEvent) {
      const el = closestImageEditable(e.target);
      if (!el) return;
      el.style.outline = "";
      hideBadge();
    }

    function ensureFileInput(): HTMLInputElement {
      if (fileInput) return fileInput;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";
      document.body.appendChild(input);
      input.addEventListener("change", onFileChange);
      fileInput = input;
      return input;
    }

    function onFileChange(this: HTMLInputElement) {
      const file = this.files && this.files[0];
      this.value = ""; // reset zodat dezelfde file opnieuw gekozen kan worden
      const el = pendingImageEl;
      const key = pendingImageKey;
      pendingImageEl = null;
      pendingImageKey = null;
      if (!file || !el || !key) return;

      el.style.opacity = IMAGE_BUSY_OPACITY;
      const existingTimer = imageBusyTimers.get(key);
      if (existingTimer) clearTimeout(existingTimer);
      const timer = setTimeout(() => {
        el.style.opacity = "";
        imageBusyTimers.delete(key);
      }, 15000);
      imageBusyTimers.set(key, timer);

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== "string") {
          el.style.opacity = "";
          const t = imageBusyTimers.get(key);
          if (t) { clearTimeout(t); imageBusyTimers.delete(key); }
          return;
        }
        window.parent.postMessage(
          { type: "SAVE_INLINE_IMAGE", id: key, dataUrl },
          window.location.origin
        );
      };
      reader.onerror = () => {
        el.style.opacity = "";
        const t = imageBusyTimers.get(key);
        if (t) { clearTimeout(t); imageBusyTimers.delete(key); }
      };
      reader.readAsDataURL(file);
    }

    function onImageClick(e: MouseEvent) {
      const el = closestImageEditable(e.target);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const key = el.getAttribute("data-i-key");
      if (!key) return;
      pendingImageEl = el;
      pendingImageKey = key;
      ensureFileInput().click();
    }

    // ---- Gedeelde override-toepassing (tekst én afbeeldingen) ----

    // Zet de src en haal een eventuele srcset weg: die wint anders van de src,
    // waardoor een vervangfoto onzichtbaar blijft zodra next/image er een zet.
    function setImageSrc(img: HTMLElement, value: string) {
      if (img.getAttribute("src") !== value) {
        img.removeAttribute("srcset");
        img.setAttribute("src", value);
      }
    }

    // Zoekt eerst [data-t-key]-matches (textContent), anders [data-i-key]-matches
    // (.src). Sleutels zijn uniek en niet-overlappend tussen de twee categorieën,
    // dus deze ene functie werkt correct voor UPDATE_PREVIEW, REVERT_INLINE_TEXT
    // én SYNC_TEXTS, voor beide soorten.
    //
    // ALLE instanties worden bijgewerkt (querySelectorAll, niet querySelector):
    // dezelfde key staat soms meer dan eens in de DOM — een categorietegel in
    // zowel het desktop-grid als de mobiele track (de eerste is op desktop
    // verborgen, dus de preview leek niet te veranderen) en global.logo in zowel
    // de header als de footer.
    function applyOverride(key: string, value: string) {
      const sel = CSS.escape(key);
      const textTargets = document.querySelectorAll(`[data-t-key="${sel}"]`);
      if (textTargets.length > 0) {
        textTargets.forEach((el) => {
          // Het element dat nu bewerkt wordt niet onder de vingers overschrijven.
          if (el instanceof HTMLElement && el !== activeEl) el.textContent = value;
        });
        return;
      }
      const imageTargets = document.querySelectorAll(`[data-i-key="${sel}"]`);
      imageTargets.forEach((el) => {
        if (el instanceof HTMLElement && "src" in el) {
          setImageSrc(el, value);
          el.style.opacity = "";
        }
      });
      if (imageTargets.length > 0) {
        const t = imageBusyTimers.get(key);
        if (t) { clearTimeout(t); imageBusyTimers.delete(key); }
      }
    }

    // Past de bekende overrides opnieuw toe op alles wat nu in de DOM staat
    // (één pass, dan opzoeken in de map). Schrijft alleen bij een verschil, dus
    // een tweede pass is een no-op en de observer kan zichzelf niet aanjagen.
    function reapplyKnownOverrides() {
      if (knownOverrides.size === 0) return;
      document.querySelectorAll("[data-t-key],[data-i-key]").forEach((el) => {
        if (!(el instanceof HTMLElement) || el === activeEl) return;
        const tekstKey = el.getAttribute("data-t-key");
        if (tekstKey !== null) {
          const val = knownOverrides.get(tekstKey);
          if (val !== undefined && el.textContent !== val) el.textContent = val;
          return;
        }
        const beeldKey = el.getAttribute("data-i-key");
        const val = beeldKey !== null ? knownOverrides.get(beeldKey) : undefined;
        if (val !== undefined && "src" in el) setImageSrc(el, val);
      });
    }

    function onMessage(e: MessageEvent) {
      // Alleen berichten van de eigen ouder-frame, op hetzelfde origin.
      if (e.source !== window.parent || e.origin !== window.location.origin) return;
      const data = e.data as { type?: string; id?: string; value?: string; items?: { key: string; value: string }[] };
      if (!data || typeof data.type !== "string") return;

      if (data.type === "UPDATE_PREVIEW" || data.type === "REVERT_INLINE_TEXT") {
        if (!data.id || typeof data.value !== "string") return;
        // Herstel = geen override meer: laat een laat gerenderd element dan
        // gewoon z'n eigen standaardwaarde houden.
        if (data.type === "UPDATE_PREVIEW") knownOverrides.set(data.id, data.value);
        else knownOverrides.delete(data.id);
        applyOverride(data.id, data.value);
        return;
      }

      // SYNC_TEXTS: de ouder stuurt dit vlak na BRIDGE_READY, met alle reeds
      // bestaande live-overrides (tekst én afbeeldingen door elkaar). LiveTexts
      // (laag 3) staat in deze modus uit, dus zonder dit bericht toont de
      // iframe altijd de statische server-fallback — nooit wat er al écht
      // live staat. Dat is precies wat de dirty-check hierboven kwetsbaar
      // maakte: een beheerder zag de fallback, niet de actieve override. Met
      // SYNC_TEXTS klopt de preview vanaf het begin, en blijft de dirty-check
      // daarmee correct.
      if (data.type === "SYNC_TEXTS" && Array.isArray(data.items)) {
        for (const it of data.items) {
          if (it && typeof it.key === "string" && typeof it.value === "string" && it.value.trim()) {
            knownOverrides.set(it.key, it.value);
            applyOverride(it.key, it.value);
          }
        }
      }
    }

    document.addEventListener("mouseover", onMouseOver, true);
    document.addEventListener("mouseout", onMouseOut, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("mouseover", onImageMouseOver, true);
    document.addEventListener("mouseout", onImageMouseOut, true);
    document.addEventListener("click", onImageClick, true);
    window.addEventListener("message", onMessage);

    // Meld aan de ouder dat de brug klaar is en scan de pagina op bewerkbare
    // tekst- én afbeeldingselementen, zodat het admin-paneel weet wat er al
    // op de pagina staat.
    //
    // Dedupliceren op key (eerste match wint): sommige componenten renderen
    // dezelfde tegel twee keer in de DOM tegelijk (bv. CategoryTileSlider —
    // een desktop-grid én een mobiele track, CSS bepaalt wat zichtbaar is).
    // Zo'n tegel draagt dan tweemaal dezelfde data-i-key/data-t-key. Zonder
    // dedupliceren krijgt het admin-paneel dubbele entries met dezelfde
    // React-key (crasht niet, maar geeft "two children with the same key"
    // en een onvoorspelbare zijpaneel-rij) — de content is sowieso identiek,
    // dus de eerste match volstaat.
    function dedupeByKey<T extends { key: string }>(list: T[]): T[] {
      const seen = new Set<string>();
      return list.filter((it) => {
        if (seen.has(it.key)) return false;
        seen.add(it.key);
        return true;
      });
    }

    function scanPage() {
      const items = dedupeByKey(
        Array.from(document.querySelectorAll("[data-t-key]")).map((node) => ({
          key: node.getAttribute("data-t-key") || "",
          text: node.textContent || "",
        }))
      );
      const images = dedupeByKey(
        Array.from(document.querySelectorAll("[data-i-key]")).map((node) => ({
          key: node.getAttribute("data-i-key") || "",
          src: node instanceof HTMLImageElement ? node.currentSrc || node.src : "",
        }))
      );
      return { items, images };
    }

    // Welke sleutels de laatste scan bevatte — om een herscan alleen door te
    // geven als er echt iets bij- of afgekomen is.
    function scanSignature(scan: ReturnType<typeof scanPage>): string {
      return scan.items.map((i) => i.key).join("|") + "#" + scan.images.map((i) => i.key).join("|");
    }

    const firstScan = scanPage();
    let lastSignature = scanSignature(firstScan);
    window.parent.postMessage({ type: "BRIDGE_READY" }, window.location.origin);
    window.parent.postMessage({ type: "PAGE_SCANNED", ...firstScan }, window.location.origin);

    // Late renders: de eerste scan draait vlak na hydratatie, maar veel
    // pagina's tekenen daarna nog (winkelmand zodra de wagen geladen is, /merken
    // en /shop na een fetch) en een klik op een link in de preview navigeert
    // client-side zonder dat de brug opnieuw start. Daarom bewaken we <main>:
    // (1) kwamen er bewerkbare sleutels bij of weg → de ouder krijgt een nieuwe
    // PAGE_SCANNED; (2) nieuwe elementen krijgen de al bekende overrides.
    // Eerst scannen, dán toepassen: een nieuw element toont nog z'n
    // standaardwaarde, en díe moet de ouder als "standaard" onthouden.
    let rescanTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      if (rescanTimer) clearTimeout(rescanTimer);
      rescanTimer = setTimeout(() => {
        const scan = scanPage();
        const signature = scanSignature(scan);
        if (signature !== lastSignature) {
          lastSignature = signature;
          window.parent.postMessage({ type: "PAGE_SCANNED", ...scan }, window.location.origin);
        }
        reapplyKnownOverrides();
        // Onze eigen wijzigingen net hierboven niet opnieuw als "nieuw" zien.
        observer.takeRecords();
      }, 250);
    });
    observer.observe(document.getElementById("main-content") ?? document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (rescanTimer) clearTimeout(rescanTimer);
      editStyle.remove();
      document.removeEventListener("mouseover", onMouseOver, true);
      document.removeEventListener("mouseout", onMouseOut, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("mouseover", onImageMouseOver, true);
      document.removeEventListener("mouseout", onImageMouseOut, true);
      document.removeEventListener("click", onImageClick, true);
      window.removeEventListener("message", onMessage);
      if (activeEl) stopEditing(activeEl);
      imageBusyTimers.forEach((t) => clearTimeout(t));
      imageBusyTimers.clear();
      badge.remove();
      if (fileInput) fileInput.remove();
    };
  }, []);

  return null;
}
